<template>
  <main class="home">
    <Header
      navbar-type="custom"
      @clicked="goBack()"
      :screenTitle="t('TRANSACTIONSHISTORY')"
    >
      <div class="col-2 p-0" @click="openFilters">
        <img
          id="filter-btn"
          :src="useAsset('img/credit-line/filter_icon.svg')"
          alt="filters"
          width="48"
          height="48"
        />
      </div>
    </Header>
    <div class="main-body">
      <!--/////////SEARCH BAR////////-->
      <div class="input-icon-container" id="search-bar-container">
        <input
          type="text"
          id="search-bar"
          @focus="onFocusSearchBar"
          @keyup="searchTransactions($event)"
          v-model="searching"
          :placeholder="t('SEARCH')"
        />
        <i class="fa fa-search" style="background: white"></i>
      </div>
      <!--/////////FILTERS////////-->
      <section id="filter-badges-container">
        <div
          class="d-flex flex-row flex-shrink-0 justify-content-start gap-2 mt-3 p-0 position-relative"
          v-if="activeFilters?.length"
        >
          <Button
            id="clear-filters-btn"
            class="text-center"
            @click="clearFilters"
          >
            <img
              :src="useAsset('img/credit-line/delete.svg')"
              alt="clear"
              class="ms-0 me-auto mb-1 p-0"
            />
            <span class="ms-1 me-0 my-auto p-0">{{
              t("CL_TRANSACTIONS.CLEAR_FILTERS_BTN")
            }}</span>
          </Button>
          <div
            class="filter-badge flex-shrink-0"
            v-for="filter in activeFilters"
            v-bind:key="filter.id"
          >
            <h3 class="mb-0">{{ filter.text }}</h3>
            <img
              :src="useAsset('img/credit-line/close_icon.svg')"
              alt="close"
              @click="removeFilter(filter.id)"
              class="p-0"
            />
          </div>
        </div>
        <hr class="mb-0 p-0 mx-0 mt-3" />
      </section>
      <!--/////////LOADING////////-->
      <div class="w-100 h-100 text-center" v-if="loading">
        <div
          class="spinner-border text-success mt-5"
          style="width: 3rem; height: 3rem; color: white"
          role="status"
        >
          <span class="visually-hidden">...</span>
        </div>
      </div>
      <!--/////////TRANSACTION LIST////////-->
      <div class="w-100 h-100" v-else>
        <div
          class="container-fluid mt-2 w-100 flex-fill px-0"
          v-if="
            transactions && transactions?.length > 0 && daysArray.length > 0
          "
        >
          <!--/////////PENDING////////-->
          <div v-if="pendingTransactions.length > 0 && !activeFilters?.length">
            <h4 class="text-black fs-6 mt-3">
              {{ t("CL_TRANSACTIONS.PENDING_TRANSACTIONS") }}
            </h4>
            <div v-for="(t, index) in pendingTransactions" v-bind:key="index">
              <hr class="m-0 p-0" />
              <TransactionItem
                :icon="getTransactionIconHelper(t.status, t.category)"
                :method="t.method"
                :amount="t.amount?.toString()"
                :type="t.type"
                show-date
                :date="t.date"
                :refund-debit="
                  getOriginalRefundDebitById(
                    t.originalDebitId,
                    transactionLists
                  )
                "
                class="w-100"
                @onclick="onClickTransaction(t)"
              />
            </div>
            <hr class="m-0 p-0" />
          </div>
          <!--/////////TRANSACTIONS////////-->
          <div v-for="(date, i) in daysArray" v-bind:key="i">
            <h4 class="text-black fs-6 mt-3">
              {{ getDateHeader(date) }}
            </h4>
            <div
              v-for="(transaction, index) in transactions"
              v-bind:key="index"
            >
              <div
                v-if="
                  compareDates(transaction.date, date) &&
                  transaction.status !== TransactionStatus.PENDING &&
                  transaction.status !== TransactionStatus.READY
                "
              >
                <hr class="m-0 p-0" />
                <TransactionItem
                  :icon="
                    getTransactionIconHelper(
                      transaction.status,
                      transaction.category
                    )
                  "
                  :method="transaction.method"
                  :amount="transaction.amount?.toString()"
                  :type="transaction.type"
                  :date="transaction.date"
                  :refund-debit="
                    getOriginalRefundDebitById(
                      transaction?.originalDebitId,
                      transactionLists
                    )
                  "
                  class="w-100"
                  @onclick="onClickTransaction(transaction)"
                />
              </div>
              <hr class="m-0 p-0" v-if="compareDates(transaction.date, date)" />
            </div>
          </div>
        </div>
        <!--/////////NO TRANSACTIONS////////-->
        <div
          class="container-fluid h-100"
          v-if="!transactions || transactions.length === 0"
        >
          <h4 class="text-center w-100" id="no-activity-text">
            {{ t("NOTRANSACTIONSSOFAR") }}
          </h4>
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import Header from "@/shared-components/consumer/Header.vue";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import TransactionItem from "@/apps/credit-line/components/Transactions/TransactionItem.vue";
import {
  ITransaction,
  TransactionStatus,
} from "@/apps/credit-line/models/CreditLineDetails";
import Button from "@/apps/credit-line/components/Button.vue";
import TransactionFilter from "@/apps/credit-line/types/TransactionFilter";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { useStore } from "vuex";
import {
  getLastMonthTransactions,
  getLastWeekTransactions,
  getOriginalRefundDebitSourceById,
  getTransactionsByCategory,
  getTransactionsInDateRange,
  populateDaysArray,
  populateTransactionsArray,
} from "@/apps/credit-line/helpers/transactions.helper";
import { formattedDate } from "@/apps/credit-line/helpers/dateFormat";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import GoogleAnalyticsEventsHelper from "@/shared-helpers/google-analytics-events.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "TransactionsHistory",
  computed: {
    TransactionStatus() {
      return TransactionStatus;
    },
  },
  methods: {
    getOriginalRefundDebitById: getOriginalRefundDebitSourceById,
    getTransactionIconHelper(
      status: TransactionStatus,
      category: ITransaction["category"]
    ) {
      switch (status) {
        case TransactionStatus.PAID:
        case TransactionStatus.ACTIVE:
        case TransactionStatus.COMPLETE:
          return category === "moneyIn"
            ? "img/credit-line/money_in.svg"
            : "img/credit-line/money_out.svg";
        case TransactionStatus.READY:
        case TransactionStatus.PENDING:
          return "img/credit-line/pending.svg";
        case TransactionStatus.REJECTED:
        case TransactionStatus.EXPIRED:
          return "img/credit-line/declined.svg";
        default:
          return "";
      }
    },
    getDateHeader(transactionDate: string): string | null {
      const today = new Date();
      const yesterday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 1
      ).toLocaleDateString();
      const currentDateValues = transactionDate
        .split("/")
        .map((x) => Number(x));
      const currentDate = new Date(
        currentDateValues[2],
        currentDateValues[1] - 1,
        currentDateValues[0]
      );

      if (today.toLocaleDateString() === transactionDate) {
        return this.t("TODAY");
      }
      if (currentDate.toLocaleDateString() === yesterday) {
        return this.t("YESTERDAY");
      }

      return formattedDate(currentDate, this.locale);
    },
    compareDates(x: number, y: string): boolean {
      return new Date(x * 1000).toLocaleDateString() === y;
    },
  },
  components: {
    Button,
    Header,
    TransactionItem,
  },
  emits: ["openFilters", "openTransactionDetails", "update:activeFilters"],
  setup(_, { emit }) {
    const { t, locale } = useI18n();
    const store = useStore();
    const transactions = ref<ITransaction[]>([]);
    const pendingTransactions = ref<ITransaction[]>([]);
    const loading = ref<boolean>(true);
    const searching = ref<string>("");
    const ogTransactions = computed(
      (): ITransaction[] => store.state.creditLine.transactionList
    );
    const activeFilters = computed(
      (): TransactionFilter[] => store.state.creditLine.transactionsFilters
    );
    const daysArray = ref<string[]>([]);
    const { fetchSummary } = useCreditLineSummary();

    onMounted(() => {
      getCreditLineSummary();
      if (activeFilters.value) {
        applyDateFilters(activeFilters.value);
        applyCategoryFilters(activeFilters.value);
      }
    });

    watch(activeFilters, (newVal) => {
      if (activeFilters.value?.length) {
        applyDateFilters(newVal);
        applyCategoryFilters(newVal);
      } else {
        transactions.value = ogTransactions.value;
      }
    });

    watch(
      () => transactions.value,
      (newVal) => (daysArray.value = populateDaysArray(newVal))
    );

    const applyDateFilters = (activeFilters: TransactionFilter[]) => {
      if (activeFilters) {
        const dateFilter = activeFilters.find((x) => x.type === "date");
        if (dateFilter) {
          if (dateFilter.id === "last_week") {
            transactions.value = getLastWeekTransactions(transactions?.value);
          } else if (dateFilter.id === "last_month") {
            transactions.value = getLastMonthTransactions(transactions?.value);
          } else {
            if (dateFilter.range) {
              transactions.value = getTransactionsInDateRange(
                transactions?.value,
                dateFilter.range
              );
            }
          }
        }
      }
    };

    const applyCategoryFilters = (activeFilters: TransactionFilter[]) => {
      if (activeFilters) {
        const categoryFilter = activeFilters.find((x) => x.type === "category");
        if (categoryFilter) {
          transactions.value = getTransactionsByCategory(
            transactions.value ?? [],
            categoryFilter?.id ?? ""
          );
        }
      }
    };

    const displayTransactionAmount = (val: string): string => {
      const newVal = Number(val);
      return newVal < 0
        ? `${currencyFormat(newVal)}`
        : `+${currencyFormat(newVal)}`;
    };

    const onClickTransaction = (transaction: ITransaction) => {
      logAnalyticsEvent(
        GoogleAnalyticsEventsHelper.CreditLine.ClickTransactionDetails,
        "Transactions"
      );
      emit("openTransactionDetails", transaction);
    };

    const getCreditLineSummary = (force = false) => {
      fetchSummary(force)
        .then((summary) => {
          if (summary) {
            store.dispatch(
              "creditLine/setTransactionsList",
              populateTransactionsArray(summary)
            );
            store.dispatch("creditLine/setCurrency", summary.currency);
            transactions.value = populateTransactionsArray(summary);
            pendingTransactions.value = transactions.value.filter(
              (x) =>
                x.status === TransactionStatus.PENDING ||
                x.status === TransactionStatus.READY
            );
            applyDateFilters(activeFilters.value);
            applyCategoryFilters(activeFilters.value);
          }
        })
        .catch()
        .finally(() => {
          if (isAndroid()) {
            Android.finishedLoading();
          }
          loading.value = false;
        });
    };

    const searchHelper = (el) => {
      let normalizedEl = el.method
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toUpperCase();
      let normalizedSearch = searching.value
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toUpperCase();
      return (
        normalizedEl.includes(normalizedSearch) ||
        String(el.amount).includes(normalizedSearch)
      );
    };

    const searchTransactions = (event: Event) => {
      searching.value = (event.target as HTMLInputElement).value;
      if (searching.value !== "") {
        transactions.value = transactions.value.filter(searchHelper);
      } else {
        transactions.value = ogTransactions.value;
      }
    };

    const goBack = () => {
      if (history.state?.fromSummary) {
        history.back();
      } else {
        Android.dismissWebView();
      }
    };

    const openFilters = () => {
      logAnalyticsEvent(
        GoogleAnalyticsEventsHelper.CreditLine.ClickFilter,
        "Transactions"
      );
      emit("openFilters");
    };

    const clearFilters = () =>
      store.dispatch("creditLine/setTransactionsFilters", null);

    const removeFilter = (filterId: TransactionFilter["id"]) => {
      let newFilterArray = activeFilters.value?.filter(
        (x) => x.id !== filterId
      );
      store.dispatch("creditLine/setTransactionsFilters", newFilterArray);
      getCreditLineSummary(true);
    };

    const onFocusSearchBar = () =>
      logAnalyticsEvent(
        GoogleAnalyticsEventsHelper.CreditLine.ClickTransactionSearchBar,
        "Transactions"
      );

    return {
      t,
      locale: locale.value,
      useAsset,
      loading,
      searching,
      daysArray,
      transactions,
      activeFilters,
      pendingTransactions,
      goBack,
      openFilters,
      removeFilter,
      clearFilters,
      onFocusSearchBar,
      onClickTransaction,
      searchTransactions,
      displayTransactionAmount,
      transactionLists: ogTransactions,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global";
.transaction-item {
  display: flex;
  justify-content: space-between;
}
#search-bar {
  width: 100%;
  height: 50px;
  padding-left: 1rem;
  box-shadow: none;
  border: none;
  outline: none;
  background: white;
  color: #888;
}
.input-icon-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #d7d7d7;
  background: white;
}
.input-icon-container .fa {
  padding: 10px;
  border: none;
  box-shadow: none;
}
.search-btn {
  float: left;
  margin-top: 4px;
  border: none;
  box-shadow: none;
}
#search-btn-container {
  margin-bottom: 15%;
  border-radius: 10px;
  box-shadow: none;
  border: 1px solid #eee;
  background: white;
}

.btn-unselected {
  color: $pj-color-green;
  border-color: $pj-color-green;
  background-color: white;
}

.btn-selected {
  color: white !important;
  border-color: $pj-color-green !important;
  background-color: $pj-color-green !important;
}

#filter-btn {
  margin: 0;
}

#no-activity-text {
  margin-top: 70%;
  color: var(--background-grey-500, #bbb);
  text-align: center;
  font-family: Inter;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
}

#clear-filters-btn {
  color: $pj-color-green;
  background: transparent;
  height: 48px;
  width: 40%;
  min-width: 40%;
  padding: 10px 3px !important;
  border-radius: 10px;
  border: 1px solid $pj-color-green;
}

.filter-badge {
  color: black;
  height: 48px;
  display: flex;
  padding: 10px !important;
  align-items: center;
  justify-content: space-between !important;
  gap: 16px !important;
  border-radius: 10px;
  border: 1px solid #eee;
  background: #fbfbfb;
}

.filter-badge h3 {
  font-size: 15px;
  font-weight: normal;
}

.filter-badge img {
  cursor: pointer;
}

#filter-badges-container {
  max-width: 100vw;
  scroll-behavior: smooth;
  overflow: scroll;
}
</style>
